import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** `full` — niemal cała szerokość okna (edytor dokumentu w ramce). */
  size?: "sm" | "md" | "lg" | "xl" | "full";
  /** Clicking the dark backdrop closes the modal. Opt-in: for a modal holding a
   * half-filled form, a stray click outside must not throw the work away. */
  closeOnBackdrop?: boolean;
}

const sizes = {
  sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-6xl", full: "max-w-[96vw]",
};

export function Modal({
  open,
  onClose,
  title,
  children,
  size = "md",
  closeOnBackdrop = false,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Capture phase on `window`: Escape reaches the modal before any handler
    // deeper in the page (an editor, a picker, a keyboard-shortcut hook) has a
    // chance to swallow the key. Closing must never depend on where focus sits.
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      data-modal-layer
      onMouseDown={(e) => {
        // Only a press that both starts AND lands on the backdrop counts — a
        // selection dragged out of the panel must not close anything.
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    >
      <div
        className={`bg-[var(--color-bg-surface)] border border-[var(--color-border)] w-full ${sizes[size]} flex flex-col max-h-[90vh]`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]">
          <h2 className="text-sm font-mono font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors duration-100"
          >
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
